import React, { useState, useEffect } from 'react';

// Utility function to format timestamps in Dublin timezone
const formatDublinTime = (timestamp) => {
  if (!timestamp) return 'Unknown';
  
  try {
    // Handle different timestamp formats
    let date;
    if (timestamp === 'Currently Connected') return timestamp;
    
    // Try parsing as ISO string or regular date string
    date = new Date(timestamp);
    
    // If invalid date, return original
    if (isNaN(date.getTime())) return timestamp;
    
    return date.toLocaleString("en-IE", {
      timeZone: "Europe/Dublin",
      year: 'numeric',
      month: '2-digit', 
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  } catch (error) {
    console.error('Error formatting timestamp:', timestamp, error);
    return timestamp;
  }
};

const ListenerTable = ({ listeners, enforcedCount }) => {
  const [sortField, setSortField] = useState('connected_seconds');
  const [sortDirection, setSortDirection] = useState('desc');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Track screen size for responsive layout
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Check if we have nginx analytics data (has timestamp) vs icecast data (has connected_seconds)
  const hasNginxData = listeners.some(l => l.timestamp);
  const durationColumnHeader = hasNginxData ? 'Connected At' : 'Duration';

  // Format duration from seconds
  const formatDuration = (seconds) => {
    const totalSeconds = parseInt(seconds) || 0;
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const remainingSeconds = totalSeconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${remainingSeconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    } else {
      return `${remainingSeconds}s`;
    }
  };

  // Format location
  const formatLocation = (location) => {
    if (!location) return 'Unknown';
    
    const parts = [];
    if (location.city && location.city !== 'Unknown') parts.push(location.city);
    if (location.region && location.region !== 'Unknown') parts.push(location.region);
    if (location.country && location.country !== 'Unknown') parts.push(location.country);
    
    return parts.length > 0 ? parts.join(', ') : 'Unknown';
  };

  // Parse user agent to extract device info
  const parseUserAgent = (userAgent) => {
    if (!userAgent) return { browser: 'Unknown Browser', os: 'Unknown OS' };
    
    const ua = userAgent.toLowerCase();
    
    // Browser detection
    let browser = 'Unknown Browser';
    if (ua.includes('firefox')) browser = 'Firefox';
    else if (ua.includes('chrome') && !ua.includes('edg')) browser = 'Chrome';
    else if (ua.includes('safari') && !ua.includes('chrome')) browser = 'Safari';
    else if (ua.includes('edg')) browser = 'Edge';
    else if (ua.includes('opera')) browser = 'Opera';
    
    // OS detection
    let os = 'Unknown OS';
    if (ua.includes('windows')) os = 'Windows';
    else if (ua.includes('mac os x')) os = 'macOS';
    else if (ua.includes('iphone')) os = 'iOS';
    else if (ua.includes('android')) os = 'Android';
    else if (ua.includes('linux')) os = 'Linux';
    
    return { browser, os };
  };

  // Format device info
  const formatDevice = (device, userAgent) => {
    // If we have nginx data with user agent, parse it
    if (userAgent && (!device || device.browser === 'Unknown Browser')) {
      const parsed = parseUserAgent(userAgent);
      return `${parsed.browser} on ${parsed.os}`;
    }
    
    if (!device) return 'Unknown';
    
    const browser = device.browser || 'Unknown Browser';
    const os = device.os || 'Unknown OS';
    
    return `${browser} on ${os}`;
  };

  // Sort listeners
  const sortedListeners = [...listeners].sort((a, b) => {
    let aValue, bValue;
    
    switch (sortField) {
      case 'ip':
        aValue = a.ip || '';
        bValue = b.ip || '';
        break;
      case 'location':
        aValue = formatLocation(a.location);
        bValue = formatLocation(b.location);
        break;
      case 'device':
        aValue = formatDevice(a.device);
        bValue = formatDevice(b.device);
        break;
      case 'connected_seconds':
        aValue = a.connected_seconds || 0;
        bValue = b.connected_seconds || 0;
        break;
      case 'timestamp':
        aValue = a.timestamp ? new Date(a.timestamp).getTime() : 0;
        bValue = b.timestamp ? new Date(b.timestamp).getTime() : 0;
        break;
      default:
        aValue = a[sortField] || '';
        bValue = b[sortField] || '';
    }
    
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    } else {
      const comparison = String(aValue).localeCompare(String(bValue));
      return sortDirection === 'asc' ? comparison : -comparison;
    }
  });

  // Handle sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Sort indicator
  const getSortIndicator = (field) => {
    if (sortField !== field) return '↕';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  // Smart text truncation for mobile
  const truncateText = (text, maxLength = 30) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Mobile Card Component
  const MobileListenerCard = ({ listener, index }) => (
    <div className="mobile-listener-card" key={listener.id || `${listener.ip}_${index}`}>
      <div className="mobile-card-header">
        <span className="mobile-ip">{listener.ip || 'Unknown'}</span>
        {listener.ip === '127.0.0.1' && (
          <span className="mobile-local-badge">Local/Proxied</span>
        )}
      </div>
      
      <div className="mobile-card-content">
        <div className="mobile-info-row">
          <span className="mobile-label">📍 Location:</span>
          <span className="mobile-value">{formatLocation(listener.location)}</span>
        </div>
        
        {listener.location && listener.location.lat != null && listener.location.lon != null && listener.location.lat !== 0 && listener.location.lon !== 0 && (
          <div className="mobile-info-row">
            <span className="mobile-label">🌐 Coords:</span>
            <span className="mobile-value mobile-coords">
              {Number(listener.location.lat).toFixed(4)}, {Number(listener.location.lon).toFixed(4)}
            </span>
          </div>
        )}
        
        <div className="mobile-info-row">
          <span className="mobile-label">💻 Device:</span>
          <span className="mobile-value">{formatDevice(listener.device, listener.user_agent)}</span>
        </div>
        
        {listener.user_agent && (
          <div className="mobile-info-row">
            <span className="mobile-label">🔧 User Agent:</span>
            <span className="mobile-value mobile-user-agent" title={listener.user_agent}>
              {truncateText(listener.user_agent, 40)}
            </span>
          </div>
        )}
        
        <div className="mobile-info-row">
          <span className="mobile-label">⏱️ {durationColumnHeader}:</span>
          <span className="mobile-value">
            {listener.timestamp ? (
              <span className="mobile-timestamp">
                {formatDublinTime(listener.timestamp)}
              </span>
            ) : (
              <span className="mobile-duration">
                {formatDuration(listener.connected_seconds || 0)}
              </span>
            )}
          </span>
        </div>
        
        <div className="mobile-info-row">
          <span className="mobile-label">🏢 ISP:</span>
          <span className="mobile-value">{listener.location?.isp || 'Unknown'}</span>
        </div>
      </div>
    </div>
  );

  if (listeners.length === 0) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '2rem', 
        color: 'var(--text-secondary)' 
      }}>
        No listeners to display
      </div>
    );
  }

  // Render mobile cards or desktop table based on screen size
  if (isMobile) {
    return (
      <div className="mobile-listeners-container">
        {/* Mobile Sort Controls */}
        <div className="mobile-sort-controls">
          <span>Sort by:</span>
          <select 
            value={sortField} 
            onChange={(e) => handleSort(e.target.value)}
            className="mobile-sort-select"
          >
            <option value="ip">IP Address</option>
            <option value="location">Location</option>
            <option value="device">Device</option>
            <option value={hasNginxData ? 'timestamp' : 'connected_seconds'}>
              {durationColumnHeader}
            </option>
          </select>
          <button 
            onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
            className="mobile-sort-direction"
          >
            {getSortIndicator(sortField)}
          </button>
        </div>

        {/* Mobile Cards */}
        <div className="mobile-cards-container">
          {sortedListeners.map((listener, index) => (
            <MobileListenerCard listener={listener} index={index} key={listener.id || `${listener.ip}_${index}`} />
          ))}
        </div>

        {/* Mobile Summary */}
        <div className="mobile-summary">
          <div className="mobile-summary-item">
            <span className="mobile-summary-label">Total Listeners</span>
            <span className="mobile-summary-value">{listeners.length}</span>
          </div>
          <div className="mobile-summary-item">
            <span className="mobile-summary-label">Avg Duration</span>
            <span className="mobile-summary-value">
              {formatDuration(
                Math.round(
                  listeners.reduce((sum, l) => sum + (l.connected_seconds || 0), 0) / listeners.length
                ) || 0
              )}
            </span>
          </div>
          <div className="mobile-summary-item">
            <span className="mobile-summary-label">Countries</span>
            <span className="mobile-summary-value">
              {new Set(
                listeners
                  .map(l => l.location?.country)
                  .filter(country => country && country !== 'Unknown')
              ).size}
            </span>
          </div>
        </div>
      </div>
    );
  }

  // Desktop Table Layout
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="listeners-table">
        <thead>
          <tr>
            <th 
              onClick={() => handleSort('ip')}
              style={{ cursor: 'pointer', userSelect: 'none' }}
            >
              IP Address {getSortIndicator('ip')}
            </th>
            <th 
              onClick={() => handleSort('location')}
              style={{ cursor: 'pointer', userSelect: 'none' }}
            >
              Location {getSortIndicator('location')}
            </th>
            <th 
              onClick={() => handleSort('device')}
              style={{ cursor: 'pointer', userSelect: 'none' }}
            >
              Device {getSortIndicator('device')}
            </th>
            <th 
              onClick={() => handleSort(hasNginxData ? 'timestamp' : 'connected_seconds')}
              style={{ cursor: 'pointer', userSelect: 'none' }}
            >
              {durationColumnHeader} {getSortIndicator(hasNginxData ? 'timestamp' : 'connected_seconds')}
            </th>
            <th>ISP</th>
          </tr>
        </thead>
        <tbody>
          {sortedListeners.map((listener, index) => (
            <tr key={listener.id || `${listener.ip}_${index}`}>
              <td data-label="IP Address">
                <span className="ip-address">
                  {listener.ip || 'Unknown'}
                </span>
              {listener.ip === '127.0.0.1' && (
                <div style={{ 
                  fontSize: '0.7rem', 
                  color: 'var(--text-muted)',
                  marginTop: '0.25rem'
                }}>
                  Local/Proxied Connection
                </div>
              )}
              </td>
              <td data-label="Location" className="location">
                {formatLocation(listener.location)}
                {listener.location && listener.location.lat != null && listener.location.lon != null && listener.location.lat !== 0 && listener.location.lon !== 0 && (
                  <div style={{ 
                    fontSize: '0.75rem', 
                    color: 'var(--text-muted)',
                    marginTop: '0.25rem'
                  }}>
                    Coords: {Number(listener.location.lat).toFixed(4)}, {Number(listener.location.lon).toFixed(4)}
                  </div>
                )}
              </td>
              <td data-label="Device">
                <div className="device-info">
                  {formatDevice(listener.device, listener.user_agent)}
                </div>
                {listener.user_agent && (
                  <div style={{ 
                    fontSize: '0.7rem', 
                    color: 'var(--text-muted)',
                    marginTop: '0.25rem',
                    fontFamily: 'monospace',
                    maxWidth: '200px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {listener.user_agent}
                  </div>
                )}
              </td>
              <td data-label="Connected/Duration">
                {listener.timestamp ? (
                  // Nginx analytics data - show connected at timestamp
                  <div>
                    <span style={{ color: 'var(--accent-secondary)', fontSize: '0.8rem' }}>
                      Connected At
                    </span>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                      {formatDublinTime(listener.timestamp)}
                    </div>
                  </div>
                ) : (
                  // Icecast data - show duration
                  <span className="duration">
                    {formatDuration(listener.connected_seconds || 0)}
                  </span>
                )}
              </td>
              <td data-label="ISP">
                <span style={{ 
                  fontSize: '0.875rem', 
                  color: 'var(--text-secondary)' 
                }}>
                  {listener.location?.isp || 'Unknown'}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Table Summary */}
      <div style={{
        marginTop: '1rem',
        padding: '0.75rem',
        background: 'var(--bg-tertiary)',
        borderRadius: '8px',
        fontSize: '0.875rem',
        color: 'var(--text-secondary)',
        display: 'flex',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div>
          <strong>{enforcedCount || listeners.length}</strong> total listeners
        </div>
        <div>
          Average duration: <strong style={{ color: 'var(--accent-secondary)' }}>
            {formatDuration(
              Math.round(
                listeners.reduce((sum, l) => sum + (l.connected_seconds || 0), 0) / listeners.length
              ) || 0
            )}
          </strong>
        </div>
        <div>
          Unique countries: <strong style={{ color: 'var(--accent-primary)' }}>
            {new Set(
              listeners
                .map(l => l.location?.country)
                .filter(country => country && country !== 'Unknown')
            ).size}
          </strong>
        </div>
      </div>
    </div>
  );
};

export default ListenerTable;